import React from 'react';
import PropTypes from 'prop-types';

import { Button } from '../../../../components/PageHeader/Button';
import Select from '../../../../components/Select';
import Strings from '../../../../Strings';
import ScriptEditor, { scriptIsValid } from './ScriptEditor';
import {
  DEFAULT_MODES,
  emptyScript,
  HAPPY_PATH,
  MODE_HTTP_ERROR,
  MODE_RESULT,
  OP_OAUTH,
  PATIENT_OPERATIONS,
  scriptFrom,
  scriptSummary,
} from './constants';
import styles from './AccessSimulator.module.scss';

const ALL_OPERATIONS = [...PATIENT_OPERATIONS, OP_OAUTH];

// The 'oauth' row is not a scripted answer but the token-endpoint policy:
// lenient/strict credential checking, or an http_error that makes
// the token endpoint fail (an auth outage). It is edited with a single Select
// instead of the full script editor, and maps onto the same stored script shape.
const OAUTH_POLICY_OPTIONS = [
  { value: 'lenient', label: Strings.accessSimOauthPolicyLenient },
  { value: 'strict', label: Strings.accessSimOauthPolicyStrict },
  { value: 'outage', label: Strings.accessSimOauthOutage },
];

const oauthPolicyFrom = script => {
  if (!script) return 'lenient';
  if (script.mode === MODE_HTTP_ERROR) return 'outage';
  return script.result_code || 'lenient';
};

const oauthScriptFromPolicy = policy => (
  policy === 'outage'
    ? { ...emptyScript(), mode: MODE_HTTP_ERROR, http_status: 503 }
    : { ...emptyScript(), mode: MODE_RESULT, result_code: policy }
);

/**
 * Organization-level default answers of the simulator: what the
 * simulator replies for a patient that has no per-patient override. The extra
 * 'oauth' row is not an answer but the token-endpoint policy: result
 * 'lenient'/'strict', or an http_error to simulate an auth outage.
 */
function OrgDefaults({ defaults, onSave, disabled }) {
  const [drafts, setDrafts] = React.useState({});

  const defaultFor = operation => defaults.find(item => item.operation === operation);

  // A row without a stored default falls back to the CMS happy path, which is
  // what the simulator does for an org that was never configured.
  const happyPath = operation => ({
    mode: 'result',
    fail_at: 'accept',
    result_code: HAPPY_PATH[operation],
    result_display: null,
    http_status: null,
    error_body: null,
    poll_202_count: 0,
  });

  const draftFor = operation => drafts[operation] ?? scriptFrom(defaultFor(operation), happyPath(operation));

  const changed = operation => {
    if (!drafts[operation]) return false;
    return scriptSummary(drafts[operation]) !== scriptSummary(defaultFor(operation))
      || drafts[operation].poll_202_count !== (defaultFor(operation)?.poll_202_count ?? 0);
  };

  return (
    <div className={styles.defaults}>
      <p className={styles.defaultsHint}>{Strings.accessSimDefaultsHint}</p>
      {ALL_OPERATIONS.map(operation => (
        <div className={styles.defaultsRow} key={operation}>
          <div className={styles.defaultsHeader}>
            <span
              className={styles.defaultsOperation}
              {...(operation === OP_OAUTH
                ? { 'data-tooltip-id': 'tooltip', 'data-tooltip-content': Strings.accessSimOauthPolicyTooltip }
                : {})}
            >
              {operation === OP_OAUTH ? Strings.accessSimOauthPolicy : operation}
            </span>
            <span className={styles.defaultsCurrent}>{scriptSummary(defaultFor(operation)) || '-'}</span>
            <Button
              title={Strings.save}
              disabled={disabled || !changed(operation) || !scriptIsValid(draftFor(operation))}
              onClick={() => onSave({ ...draftFor(operation), operation })}
              testid={`save-default-${operation}`}
            />
          </div>
          {operation === OP_OAUTH ? (
            <div className={styles.defaultsOauth}>
              <Select
                id="default-oauth-policy"
                label={Strings.accessSimOauthPolicy}
                data={OAUTH_POLICY_OPTIONS}
                value={oauthPolicyFrom(draftFor(operation))}
                onChange={option => setDrafts({ ...drafts, [OP_OAUTH]: oauthScriptFromPolicy(option?.value) })}
                disabled={disabled}
              />
            </div>
          ) : (
            <ScriptEditor
              id={`default-${operation}`}
              operation={operation}
              value={draftFor(operation)}
              modes={DEFAULT_MODES}
              showTimesRemaining={false}
              disabled={disabled}
              onChange={draft => setDrafts({ ...drafts, [operation]: draft })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

OrgDefaults.propTypes = {
  defaults: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

OrgDefaults.defaultProps = {
  defaults: [],
};

export default OrgDefaults;
